<script setup>
import { ref } from 'vue';
import NavigationBar from '@/components/NavigationBar.vue';
import SelectYandexAccountModal from '@/components/YandexDirectStatistics/SelectYandexAccountModal.vue';
import { Api } from '@/service/api';
import { createToaster } from '@meforma/vue-toaster';

const toaster = createToaster();

const externalLink = ref('');
const selectedAccount = ref(null);
const isAccModalVisible = ref(false);
const isLoading = ref(false);
const responseData = ref(null);
const errorData = ref(null);

function updateAccount(account) {
    selectedAccount.value = account;
}

async function submit() {
    if (!selectedAccount.value) {
        toaster.error('Выберите Yandex аккаунт');
        return;
    }

    if (!externalLink.value.trim()) {
        toaster.error('Введите ссылку');
        return;
    }

    isLoading.value = true;
    responseData.value = null;
    errorData.value = null;

    try {
        responseData.value = await Api.getYandexFiles(
            selectedAccount.value.id,
            externalLink.value.trim()
        );
    } catch (error) {
        errorData.value = error.response?.data || {
            message: error.message || 'Не удалось получить ответ от сервера',
        };
        toaster.error('Ошибка запроса');
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
    <div class="flex">
        <NavigationBar />
        <div class="min-h-screen w-full bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div class="max-w-3xl mx-auto">
                <header class="mb-8">
                    <h1 class="text-2xl font-bold text-gray-900">Yandex Files Test</h1>
                    <p class="text-sm text-gray-500 mt-1">
                        Тестовый запрос на <code class="text-gray-700">GET /yandex/files</code>
                    </p>
                </header>

                <div class="bg-white rounded-xl border border-gray-100 p-6 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] space-y-6">
                    <div>
                        <p class="text-sm font-medium text-gray-700 mb-2">Yandex аккаунт</p>
                        <div class="rounded-lg border border-gray-200 flex items-center justify-between p-3 gap-4">
                            <div class="flex items-center gap-3 min-w-0">
                                <div
                                    v-if="selectedAccount?.default_avatar_id"
                                    class="size-10 rounded-full overflow-hidden shrink-0"
                                >
                                    <img
                                        :src="'https://avatars.yandex.net/get-yapic/' + selectedAccount.default_avatar_id + '/islands-200'"
                                        :alt="selectedAccount.display_name"
                                        class="w-full h-full object-cover"
                                    />
                                </div>
                                <div v-else class="size-10 rounded-full bg-blue-500 shrink-0"></div>
                                <span class="text-sm font-medium text-gray-900 truncate">
                                    {{ selectedAccount?.display_name || 'Аккаунт не выбран' }}
                                </span>
                            </div>
                            <button
                                type="button"
                                class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shrink-0"
                                @click="isAccModalVisible = true"
                            >
                                Выбрать аккаунт
                            </button>
                        </div>
                    </div>

                    <div>
                        <label for="external-link" class="block text-sm font-medium text-gray-700 mb-2">
                            External link
                        </label>
                        <input
                            id="external-link"
                            v-model="externalLink"
                            type="text"
                            placeholder="Вставьте ссылку"
                            class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            @keyup.enter="submit"
                        />
                    </div>

                    <button
                        type="button"
                        class="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        :disabled="isLoading"
                        @click="submit"
                    >
                        {{ isLoading ? 'Отправка...' : 'Отправить' }}
                    </button>
                </div>

                <div v-if="responseData" class="mt-6 bg-white rounded-xl border border-gray-100 p-6">
                    <h2 class="text-sm font-semibold text-gray-900 mb-3">Ответ бэкенда</h2>
                    <pre class="text-xs text-gray-800 bg-gray-50 rounded-lg p-4 overflow-auto max-h-[480px]">{{ JSON.stringify(responseData, null, 2) }}</pre>
                </div>

                <div v-if="errorData" class="mt-6 bg-white rounded-xl border border-red-100 p-6">
                    <h2 class="text-sm font-semibold text-red-700 mb-3">Ошибка</h2>
                    <pre class="text-xs text-red-800 bg-red-50 rounded-lg p-4 overflow-auto max-h-[480px]">{{ JSON.stringify(errorData, null, 2) }}</pre>
                </div>
            </div>
        </div>
    </div>

    <SelectYandexAccountModal
        v-model:is-visible="isAccModalVisible"
        @update:account="updateAccount"
    />
</template>
